import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import logger from '../utils/logger';
import { Theater, Users, Wrench, Film, FileText, Briefcase, ClipboardList, Lock, PartyPopper, Clock, CheckCircle, XCircle, FolderOpen, AlertTriangle, Lightbulb, Send } from 'lucide-react';

const ApplyTask = () => {
  const { taskId } = useParams();
  const [task, setTask] = useState(null);
  const [form, setForm] = useState({ nom: '', contacts: '', infos: '', motivation: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [hasAlreadyApplied, setHasAlreadyApplied] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [isRoleAssigned, setIsRoleAssigned] = useState(false);
  const navigate = useNavigate();
    const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const res = await api.get(`/tasks/${taskId}`);
        setTask(res.data);
        
        // Vérifier si le rôle est déjà assigné
        if (res.data.assigned_user_id) {
          setIsRoleAssigned(true);
        }
        
        // Vérifier si l'utilisateur a déjà postulé
        if (user && res.data.project_id) {
          try {
            const appRes = await api.get(`/applications/project/${res.data.project_id}`);
            const myApp = appRes.data.find(app => String(app.task_id) === String(taskId));
            if (myApp) {
              setHasAlreadyApplied(true);
              setExistingApplication(myApp);
            }
          } catch (err) {
            // Silencieux en production
          }
        }
      } catch (err) {
        logger.error(err);
      }
    };
    fetchTask();
    if (user) {
      setForm(prev => ({ ...prev, nom: user.nom || '' }));
    }
  }, [taskId, user]);



  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'motivation') {
      setCharCount(value.length);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    
    if (form.motivation.length < 50) {
      setError('La motivation doit contenir au moins 50 caractères');
      return;
    }

    setLoading(true);
    try {
      await api.post('/applications', { ...form, task_id: taskId });
      setLoading(false);
      setSuccess(true);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Erreur lors de la candidature');
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      acteur: <Theater className="w-8 h-8 text-indigo-600" />,
      figurant: <Users className="w-8 h-8 text-indigo-600" />,
      technicien: <Wrench className="w-8 h-8 text-indigo-600" />,
      realisateur: <Film className="w-8 h-8 text-indigo-600" />,
      scenariste: <FileText className="w-8 h-8 text-indigo-600" />,
      producteur: <Briefcase className="w-8 h-8 text-indigo-600" />,
      autre: <ClipboardList className="w-8 h-8 text-indigo-600" />
    };
    return icons[type] || <ClipboardList className="w-8 h-8 text-indigo-600" />;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col items-center justify-center py-10 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Lock className="w-16 h-16 mx-auto mb-4 text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Connexion requise</h2>
          <p className="text-gray-600 mb-6">Vous devez être connecté pour postuler.</p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition"
            >
              'Se connecter'
            </Link>
            <Link
              to="/register"
              className="block w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              'Créer un compte'
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col items-center justify-center py-10 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <PartyPopper className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h2 className="text-2xl font-bold text-green-600 mb-4">Candidature envoyée !</h2>
          <p className="text-gray-600 mb-6">{'Votre candidature a été envoyée avec succès. L\'auteur du projet vous contactera bientôt.'}</p>
          <div className="space-y-3">
            <Link
              to="/my-applications"
              className="block w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition"
            >
              'Voir mes candidatures'
            </Link>
            <Link
              to="/"
              className="block w-full px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              'Retour au feed'
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Afficher un message si le rôle est déjà assigné
  if (isRoleAssigned && task) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col items-center justify-center py-10 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Lock className="w-16 h-16 mx-auto mb-4 text-gray-500" />
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Rôle déjà pourvu</h2>
          <p className="text-gray-600 mb-6">Ce rôle a déjà été assigné à quelqu'un d'autre. Vous ne pouvez plus postuler.</p>
          <div className="space-y-3">
            <Link
              to={`/project/${task.project_id}`}
              className="block w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition"
            >
              Retour au projet
            </Link>
            <Link
              to="/projects"
              className="block w-full px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              Voir d'autres projets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Afficher un message si l'utilisateur a déjà postulé
  if (hasAlreadyApplied && existingApplication && task) {
    const statusLabels = {
      en_attente: { icon: <Clock className="w-6 h-6" />, label: 'En attente', color: 'text-yellow-600', bg: 'bg-yellow-100' },
      accepte: { icon: <CheckCircle className="w-6 h-6" />, label: 'Acceptée', color: 'text-green-600', bg: 'bg-green-100' },
      refuse: { icon: <XCircle className="w-6 h-6" />, label: 'Refusée', color: 'text-red-600', bg: 'bg-red-100' }
    };
    const status = statusLabels[existingApplication.statut] || statusLabels.en_attente;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col items-center justify-center py-10 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-4 flex justify-center">{status.icon && React.cloneElement(status.icon, { className: 'w-16 h-16' })}</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Vous avez déjà postulé</h2>
          <p className="text-gray-600 mb-4">Vous avez déjà soumis une candidature pour ce rôle.</p>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${status.bg} ${status.color} font-medium mb-6`}>
            {status.icon} Statut : {status.label}
          </div>
          <div className="space-y-3">
            <Link
              to="/my-applications"
              className="block w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition"
            >
              Voir mes candidatures
            </Link>
            <Link
              to={`/project/${task.project_id}`}
              className="block w-full px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
            >
              Retour au projet
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <button onClick={() => navigate(-1)} className="text-indigo-600 hover:underline flex items-center gap-2">
            ← 'Retour'
          </button>
        </div>

        {/* Task Info */}
        {task && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <span className="text-4xl">{getTypeIcon(task.type)}</span>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-1">{task.titre}</h2>
                <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-2">
                  {task.type}
                </span>
                <p className="text-gray-600">{task.description}</p>
                {task.project_titre && (
                  <Link to={`/project/${task.project_id}`} className="text-indigo-600 text-sm hover:underline mt-2 inline-flex items-center gap-1">
                    <FolderOpen className="w-4 h-4" /> {task.project_titre}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Application Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-indigo-700 mb-6">Postuler</h2>
          
          {error && (
            <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                'Votre nom' *
              </label>
              <input
                name="nom"
                type="text"
                placeholder='Jean Dupont'
                value={form.nom}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
              />
            </div>

            {/* Contacts */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                'Moyens de contact' *
              </label>
              <input
                name="contacts"
                type="text"
                placeholder='Email, téléphone, réseaux sociaux...'
                value={form.contacts}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
              />
              <p className="text-xs text-gray-500 mt-1">{'Comment l\'auteur peut vous joindre'}</p>
            </div>

            {/* Personal/Professional Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                'Infos personnelles & professionnelles' *
              </label>
              <textarea
                name="infos"
                placeholder='Parlez de vous, votre expérience, vos compétences...'
                value={form.infos}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 transition resize-none"
              />
            </div>

            {/* Motivation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                'Motivation' * <span className="text-gray-400 font-normal">(min. 50 caractères)</span>
              </label>
              <textarea
                name="motivation"
                placeholder='Pourquoi souhaitez-vous participer à ce projet ?'
                value={form.motivation}
                onChange={handleChange}
                required
                rows={5}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 transition resize-none ${
                  charCount > 0 && charCount < 50 ? 'border-yellow-400' : 'border-gray-200'
                }`}
              />
              <div className="flex justify-between items-center mt-1">
                <p className={`text-xs ${charCount < 50 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {charCount}/50 caractères minimum
                </p>
                {charCount >= 50 && <span className="text-green-600 text-xs">✓</span>}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-indigo-50 rounded-lg p-4">
              <h4 className="font-medium text-indigo-700 mb-2 flex items-center gap-2"><Lightbulb className="w-5 h-5" /> 'Conseils'</h4>
              <ul className="text-sm text-indigo-600 space-y-1">
                <li>• 'Soyez précis sur votre expérience'</li>
                <li>• 'Montrez votre enthousiasme pour le projet'</li>
                <li>• 'Mentionnez vos disponibilités'</li>
              </ul>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || charCount < 50}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  'Envoi...'
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" /> 'Envoyer ma candidature'
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplyTask;
